/* Image previews only. Existing option inputs, prices and purchase handlers stay untouched. */
(() => {
  'use strict';
  if (window.KaliaImageZoom) return;
  const rootSelector = 'kalia-product-studio,kalia-linen-complete,kalia-drapery,kalia-hardware,[data-free-swatches]';
  const choices = '.kps-swatch,.kps-choice,.kd-choice,.kfs-card,.kdh-model,.kdh-option-choice,.kd-thumb,.khw-thumbnail,.khw-choice--visual,[data-photo-url]';
  const roots = new WeakMap();
  let activePreview;

  class KaliaImageZoom {
    static safeUrl(value) {
      if (typeof value !== 'string' || !value.trim()) return '';
      try {
        const url = new URL(value.trim(), document.baseURI || window.location.href);
        return /^https?:$/.test(url.protocol) && !url.username && !url.password ? url.href : '';
      } catch (_) { return ''; }
    }

    static detailUrl(value) {
      const safe = this.safeUrl(value);
      if (!safe) return '';
      const url = new URL(safe);
      // This is the same published Cloudflare variant used by the shade photo viewer.
      if (url.hostname === 'imagedelivery.net') url.pathname = url.pathname.replace(/\/thumbnail\/?$/, '/large');
      // Ask Shopify for more pixels of this exact image, retaining its version and identity.
      if (url.hostname === 'cdn.shopify.com' || /\.myshopify\.com$/.test(url.hostname) && url.pathname.startsWith('/cdn/shop/')) {
        if (url.searchParams.has('width') || url.searchParams.has('height') || url.searchParams.has('crop')) {
          const width = Math.max(1800, Number(url.searchParams.get('width')) || 0, Number(url.searchParams.get('height')) || 0);
          ['width', 'height', 'crop'].forEach(key => url.searchParams.delete(key));
          url.searchParams.set('width', String(width));
        }
      }
      return url.href;
    }

    static preview(control, image) {
      const radio = control.querySelector('input[type="radio"]');
      const root = control.closest(rootSelector);
      let explicit = image.dataset.zoomSrc || control.dataset.zoomSrc || control.dataset.galleryImage || control.dataset.hwImage || control.dataset.photoUrl;
      // Some drapery catalogs publish a separate original for the same color.
      if (!explicit && radio && root?.config) {
        const list = { color: 'colors', header: 'headers', lining: 'linings' }[radio.name];
        const option = list && root.config[list]?.[Number(radio.value)];
        if (option?.preview) explicit = option.preview;
      }
      const fallback = image.dataset.zoomFallback === 'none' || control.dataset.zoomFallback === 'none' ? '' : this.safeUrl(image.getAttribute('src') || image.currentSrc);
      const src = this.detailUrl(explicit) || this.detailUrl(fallback);
      const label = String(control.title || radio?.getAttribute('aria-label') || control.querySelector('.kfs-card__color,strong,.kd-option-label')?.textContent || image.alt || control.getAttribute('aria-label') || 'Image detail').trim();
      return { src, fallback, label: label.slice(0, 240) || 'Image detail' };
    }

    constructor(root) {
      this.root = root;
      this.controls = new WeakMap();
      this.scan();
      this.observer = new MutationObserver(() => {
        if (this.queued) return;
        this.queued = true;
        queueMicrotask(() => { this.queued = false; this.scan(); });
      });
      this.observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'data-zoom-src', 'data-zoom-fallback'] });
    }

    node(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    }

    icon(button) {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('aria-hidden', 'true'); svg.setAttribute('focusable', 'false');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M21 21l-5.4-5.4M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0ZM6 10h8M10 6v8');
      svg.append(path); button.append(svg);
    }

    enhance(control, image, kind) {
      const existing = this.controls.get(control);
      if (!image) { if (existing) existing.disabled = true; return; }
      const preview = this.constructor.preview(control, image);
      if (existing) {
        const label = `Enlarge ${preview.label}`;
        if (existing.getAttribute('aria-label') !== label) { existing.setAttribute('aria-label', label); existing.title = label; }
        existing.disabled = !preview.src;
        return;
      }
      if (!preview.src) return;
      const wrapper = this.node('div', `kiz-wrap kiz-${kind}`);
      const button = this.node('button', 'kiz-open'); button.type = 'button';
      button.setAttribute('aria-label', `Enlarge ${preview.label}`); button.title = `Enlarge ${preview.label}`;
      button.setAttribute('aria-haspopup', 'dialog');
      this.icon(button);
      const caption = this.node('span', 'kiz-open-label', 'View image'); button.append(caption);
      // A sibling button is never inside a radio label or another selection button.
      control.parentNode.insertBefore(wrapper, control); wrapper.append(control, button);
      button.addEventListener('click', event => {
        event.preventDefault(); event.stopPropagation();
        const currentImage = control.querySelector('img');
        if (currentImage) this.open(this.constructor.preview(control, currentImage), button);
      });
      this.controls.set(control, button);
    }

    scan() {
      this.root.querySelectorAll(choices).forEach(control => {
        const image = control.querySelector('img');
        const kind = control.matches('.kps-swatch,.kd-swatch') ? 'swatch'
          : control.matches('.kfs-card,.kdh-model') ? 'card'
          : control.matches('.kd-thumb,.khw-thumbnail,[data-photo-url]') ? 'gallery' : 'choice';
        this.enhance(control, image, kind);
      });
      this.root.querySelectorAll('.kfs-selected-item .kfs-card__media,.klc-fabric').forEach(control => {
        this.enhance(control, control.querySelector('img'), control.matches('.klc-fabric') ? 'fabric' : 'selected');
      });
    }

    ensureDialog() {
      if (this.dialog) return;
      const dialog = this.node('dialog', 'kiz-dialog');
      const titleId = `KaliaImageZoomTitle-${++KaliaImageZoom.sequence}`;
      const noteId = `${titleId}-note`, statusId = `${titleId}-status`;
      dialog.setAttribute('aria-labelledby', titleId); dialog.setAttribute('aria-describedby', `${noteId} ${statusId}`);
      const panel = this.node('div', 'kiz-panel');
      const header = this.node('header', 'kiz-header');
      this.title = this.node('h2', 'kiz-title'); this.title.id = titleId;
      const close = this.node('button', 'kiz-close', 'Close'); close.type = 'button'; close.autofocus = true;
      close.setAttribute('aria-label', 'Close image preview'); close.addEventListener('click', () => this.close());
      header.append(this.title, close);
      this.stage = this.node('div', 'kiz-stage'); this.stage.tabIndex = 0; this.stage.setAttribute('aria-label', 'Image preview. Scroll to inspect the full size image.');
      const footer = this.node('footer', 'kiz-footer');
      this.note = this.node('p', 'kiz-note', 'Screen colors can vary. Compare fabric with a physical swatch.'); this.note.id = noteId;
      this.status = this.node('p', 'kiz-status'); this.status.id = statusId; this.status.setAttribute('role', 'status');
      this.fullSize = this.node('button', 'kiz-full-size', 'View full size'); this.fullSize.type = 'button';
      this.fullSize.setAttribute('aria-pressed', 'false');
      this.fullSize.addEventListener('click', () => {
        const full = this.fullSize.getAttribute('aria-pressed') !== 'true';
        this.stage.classList.toggle('kiz-stage--full', full); this.fullSize.setAttribute('aria-pressed', String(full));
        this.fullSize.textContent = full ? 'Fit to screen' : 'View full size';
      });
      footer.append(this.note, this.status, this.fullSize); panel.append(header, this.stage, footer); dialog.append(panel);
      dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.close();
      });
      dialog.addEventListener('close', () => this.restoreFocus());
      document.body.append(dialog); this.dialog = dialog;
    }

    open(preview, trigger) {
      if (!preview.src) return;
      if (activePreview && activePreview !== this) activePreview.close();
      this.ensureDialog(); this.trigger = trigger;
      this.title.textContent = preview.label;
      this.status.textContent = 'Loading image…'; this.fullSize.disabled = true;
      this.fullSize.textContent = 'View full size'; this.fullSize.setAttribute('aria-pressed', 'false');
      this.stage.classList.remove('kiz-stage--full'); this.stage.scrollTop = 0; this.stage.scrollLeft = 0;
      const image = this.node('img', 'kiz-image'); image.alt = preview.label; image.decoding = 'async';
      let usingFallback = false;
      image.addEventListener('load', () => {
        if (this.image !== image || !this.dialog.open) return;
        const width = image.naturalWidth, height = image.naturalHeight;
        this.status.textContent = width && height && width < 600 && height < 600
          ? `Source image: ${width} × ${height} px. Detail is limited by the supplied image.` : '';
        this.fullSize.disabled = !(width > image.clientWidth + 1 || height > image.clientHeight + 1);
      });
      image.addEventListener('error', () => {
        if (this.image !== image || !this.dialog.open) return;
        if (!usingFallback && preview.fallback && preview.fallback !== preview.src) {
          usingFallback = true; image.src = preview.fallback; return;
        }
        image.hidden = true; this.fullSize.disabled = true;
        this.status.textContent = 'This image could not be loaded. Close this preview and try again.';
      });
      this.image = image; this.stage.replaceChildren(image);
      if (!this.dialog.open) this.dialog.showModal();
      activePreview = this; document.documentElement.classList.add('kiz-modal-open');
      image.src = preview.src;
    }

    close() { if (this.dialog?.open) this.dialog.close(); }
    restoreFocus() {
      this.image = null;
      if (activePreview === this) { activePreview = null; document.documentElement.classList.remove('kiz-modal-open'); }
      if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
      else if (this.root.isConnected) {
        const previous = this.root.getAttribute('tabindex'); this.root.tabIndex = -1; this.root.focus({ preventScroll: true });
        if (previous === null) this.root.removeAttribute('tabindex'); else this.root.setAttribute('tabindex', previous);
      }
      this.trigger = null;
    }

    destroy() {
      this.observer.disconnect(); this.close(); this.dialog?.remove();
    }
  }
  KaliaImageZoom.sequence = 0;
  window.KaliaImageZoom = KaliaImageZoom;
  const attach = root => {
    if (!root || roots.has(root) || typeof HTMLDialogElement === 'undefined' || typeof HTMLDialogElement.prototype.showModal !== 'function') return;
    roots.set(root, new KaliaImageZoom(root));
  };
  const start = () => document.querySelectorAll(rootSelector).forEach(attach);
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll(rootSelector).forEach(root => { roots.get(root)?.destroy(); roots.delete(root); });
  });
  // The existing selected-fabric control now opens the same preview in place.
  document.addEventListener('click', event => {
    const trigger = event.target.closest?.('[data-show-fabric]');
    const root = trigger?.closest('kalia-product-studio');
    const instance = root && roots.get(root), image = root?.querySelector('[data-selected-swatch]');
    if (!instance || !image) return;
    const preview = KaliaImageZoom.preview(trigger, image);
    if (!preview.src) return;
    event.preventDefault(); event.stopImmediatePropagation(); instance.open(preview, trigger);
  }, true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();
